import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { FYLKER } from '../components/LocationSelector';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const EditProfilePage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, updateUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    location_fylke: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        location_fylke: user.location_fylke || ''
      });
    }
  }, [user]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block">
            lock
          </span>
          <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
            Du må være logget inn
          </h2>
          <Link 
            to="/logg-inn"
            className="inline-block bg-[#0A1929] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors"
          >
            Logg inn
          </Link>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`${API_URL}/users/${user.id}`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser(response.data);
      setSuccess('Profilen er oppdatert');
      
      setTimeout(() => {
        navigate(`/profil/${user.id}`);
      }, 1500);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.response?.data?.message || 'Kunne ikke oppdatere profilen. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Link 
            to={`/profil/${user?.id}`}
            className="inline-flex items-center gap-1 text-[#1E3A5F] hover:text-[#0A1929] mb-4 transition-colors"
          >
            <span className="material-symbols-outlined">arrow_back</span>
            Tilbake til profil
          </Link>
          <h1 className="font-heading font-bold text-2xl text-[#0A1929]">
            Rediger profil
          </h1>
          <p className="text-slate-500 mt-1">
            Oppdater din profilinformasjon
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined">error</span>
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 text-green-600 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined">check_circle</span>
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Navn */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Navn
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent transition-all"
                placeholder="Ditt fulle navn"
              />
            </div>

            {/* Telefon */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Telefonnummer
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent transition-all"
                placeholder="12345678"
              />
              <p className="text-xs text-slate-500 mt-1">
                Valgfritt. Brukes for kontakt om oppdrag.
              </p>
            </div>

            {/* Fylke */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                Fylke
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400">
                  location_on
                </span>
                <select
                  name="location_fylke"
                  value={formData.location_fylke}
                  onChange={handleChange}
                  className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent transition-all appearance-none bg-white"
                >
                  <option value="">Velg fylke</option>
                  {FYLKER.map(fylke => (
                    <option key={fylke.id} value={fylke.id}>{fylke.name}</option>
                  ))}
                </select>
                <span className="absolute right-3 top-1/2 -translate-y-1/2 material-symbols-outlined text-slate-400 pointer-events-none">
                  expand_more
                </span>
              </div>
            </div>

            {/* E-post (ikke redigerbar her) */}
            <div>
              <label className="block text-sm font-medium text-[#0A1929] mb-2">
                E-post
              </label>
              <input
                type="email"
                value={user?.email || ''}
                disabled
                className="w-full px-4 py-3 border border-slate-200 rounded-lg bg-slate-50 text-slate-500 cursor-not-allowed"
              />
              <p className="text-xs text-slate-500 mt-1">
                For å endre e-post, gå til <Link to="/innstillinger" className="text-[#1E3A5F] hover:underline">Innstillinger</Link>
              </p>
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <Link
                to={`/profil/${user?.id}`}
                className="flex-1 bg-slate-100 text-[#0A1929] py-3 rounded-lg font-medium hover:bg-slate-200 transition-colors text-center"
              >
                Avbryt
              </Link>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-[#0A1929] text-white py-3 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <span className="animate-spin material-symbols-outlined">progress_activity</span>
                    Lagrer...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined">save</span>
                    Lagre endringer
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfilePage;
